import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { skeletonGradient } from '~/searchCio/utils/styles';

// GridWrapper for handling the grid layout of placeholders
const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  grid-gap: ${({ $verticalGutter, $horizontalGutter }) =>
    `${$verticalGutter}px ${$horizontalGutter}px`};
  width: 100%;
`;

// ImagePlaceholder for displaying the skeleton image with dynamic aspect ratio
const ImagePlaceholder = styled.div`
  ${skeletonGradient}
  width: 100%;
  padding-top: ${({ $imagePaddingTop }) => $imagePaddingTop}%;
`;

// TextPlaceholder for displaying the skeleton text with fixed height
const TextPlaceholder = styled.div`
  ${skeletonGradient}
  width: 100%;
  height: ${({ $textHeight }) => $textHeight}px;
  margin-top: 8px;
`;

/**
 * ProductCardsSkeletonLoader component renders image and text placeholders in a grid format.
 * It uses CSS grid to manage the layout dynamically based on the provided props.
 *
 * @param {number} rows - Number of rows of placeholders.
 * @param {number} columns - Number of columns per row.
 * @param {number} imageAspectRatio - Aspect ratio of the image placeholders.
 * @param {number} verticalGutter - Vertical space (px) between columns.
 * @param {number} horizontalGutter - Horizontal space (px) between rows.
 * @param {number} textHeight - Height of the text placeholder in pixels.
 */
const ProductCardsSkeletonLoader = ({
  rows = 1,
  columns = 4,
  imageAspectRatio = 9 / 16,
  verticalGutter = 16,
  horizontalGutter = 16,
  textHeight = 40,
}) => {
  const totalPlaceholders = rows * columns;

  const imagePaddingTop = 100 / imageAspectRatio;
  return (
    <GridWrapper
      $columns={columns}
      $verticalGutter={verticalGutter}
      $horizontalGutter={horizontalGutter}
    >
      {Array.from({ length: totalPlaceholders }, (_, index) => (
        <div key={index}>
          <ImagePlaceholder $imagePaddingTop={imagePaddingTop} />
          <TextPlaceholder $textHeight={textHeight} />
        </div>
      ))}
    </GridWrapper>
  );
};

ProductCardsSkeletonLoader.propTypes = {
  columns: PropTypes.number.isRequired,
  horizontalGutter: PropTypes.number,
  imageAspectRatio: PropTypes.number.isRequired,
  rows: PropTypes.number.isRequired,
  textHeight: PropTypes.number.isRequired,
  verticalGutter: PropTypes.number,
};

export default ProductCardsSkeletonLoader;
